import posthog, { Properties } from 'posthog-js'
import type {
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
  Router,
} from 'vue-router'
import { User } from '~/types/users'
import { COOKIE_CONSENT_COOKIE_NAME } from '~/constants/storage'
import { DEFAULT_POSTHOG_HOST } from '~/constants/analytics'

let initialized = false
let pageViewRouter: Router | undefined
let currentUser: Pick<User, 'id' | 'roles'> | undefined

function getApiHostname() {
  try {
    return new URL(import.meta.env.VITE_API_URL).hostname
  } catch {
    return undefined
  }
}

function hasAnalyticsConsent() {
  const cookie = document.cookie
    .split('; ')
    .find((value) => value.startsWith(`${COOKIE_CONSENT_COOKIE_NAME}=`))

  if (!cookie) return false

  try {
    const consent = JSON.parse(
      decodeURIComponent(cookie.slice(COOKIE_CONSENT_COOKIE_NAME.length + 1)),
    )
    return consent?.categories?.analytics === true
  } catch {
    return false
  }
}

function getRouteTemplate(route: RouteLocationNormalized) {
  return route.matched.at(-1)?.path || route.path
}

function getSurface(routeName: string) {
  if (routeName.startsWith('subscription-portal')) return 'subscription_portal'
  if (routeName.startsWith('checkout')) return 'checkout'
  if (routeName.startsWith('user-partner-onboarding'))
    return 'user_partner_onboarding'
  if (routeName.startsWith('partner-onboarding')) return 'partner_onboarding'
  return routeName.split('-')[0] || 'unknown'
}

function identifyCurrentUser() {
  if (!initialized || !currentUser || !posthog.is_capturing()) return

  posthog.identify(currentUser.id, {
    roles: currentUser.roles ?? [],
  })
}

export function captureAnalyticsPageView(
  route: RouteLocationNormalized | RouteLocationNormalizedLoaded,
) {
  if (!initialized || !posthog.is_capturing() || !route.name) return

  const routeName = route.name.toString()
  const routeTemplate = getRouteTemplate(route)

  posthog.capture('$pageview', {
    $current_url: `${window.location.origin}${routeTemplate}`,
    $pathname: routeTemplate,
    environment: import.meta.env.MODE,
    route_name: routeName,
    route_template: routeTemplate,
    surface: getSurface(routeName),
  })
}

export function setAnalyticsConsent(enabled: boolean) {
  if (!initialized) return

  const wasCapturing = posthog.is_capturing()

  if (!enabled) {
    posthog.opt_out_capturing()
    return
  }

  posthog.opt_in_capturing({ captureEventName: false })
  identifyCurrentUser()

  if (!wasCapturing && pageViewRouter) {
    captureAnalyticsPageView(pageViewRouter.currentRoute.value)
  }
}

export function initializeAnalytics(router: Router) {
  const enabled = import.meta.env.VITE_POSTHOG_ENABLED === 'true'
  const projectToken = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN

  if (!enabled || !projectToken || initialized) return

  const apiHostname = getApiHostname()

  posthog.init(projectToken, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || DEFAULT_POSTHOG_HOST,
    defaults: '2026-06-25',
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    opt_out_capturing_by_default: true,
    opt_out_persistence_by_default: true,
    person_profiles: 'identified_only',
    tracing_headers: apiHostname ? [apiHostname] : [],
  })

  initialized = true
  pageViewRouter = router
  setAnalyticsConsent(hasAnalyticsConsent())

  router.afterEach((to, _from, failure) => {
    if (!failure) captureAnalyticsPageView(to)
  })
}

export function identifyAnalyticsUser(user: Pick<User, 'id' | 'roles'>) {
  if (!user.id) return

  currentUser = user
  identifyCurrentUser()
}

export function resetAnalyticsUser() {
  currentUser = undefined
  if (!initialized) return

  posthog.reset()
}

export function captureAnalyticsEvent(event: string, properties?: Properties) {
  if (!initialized) return

  posthog.capture(event, properties)
}
